import { z } from 'zod';
import type { CamundaFormValues } from '@/lib/camunda/types';
import { NET4_FIELD_LIMITS, net4PhoneSchema, net4TextSchema, compactNet4Phone } from '../shared/net4-field-limits';
import { clientIdentificationError, normalizeClientIdentification } from './client-identification';
import { normalizeSecuritySocialNumber, securitySocialKind } from '../shared/net4-security-social-number';
import { isEttProductFamily, NET4_CLIENT_FIELDS } from './rpa-client-data-utils';

export const optionalFormText = (schema: z.ZodString) =>
  schema.nullable().transform((value) => value === null ? '' : value).optional();

export const closedWonCompanyFields = {
  razon_social: optionalFormText(net4TextSchema(NET4_FIELD_LIMITS.companyName)),
  nombre_comercial: optionalFormText(net4TextSchema(NET4_FIELD_LIMITS.companyName)),
  pais: optionalFormText(z.string().trim()),
  provincia: optionalFormText(z.string().trim()),
  provincia_label: optionalFormText(z.string()),
  municipio: optionalFormText(z.string().trim()),
  localidad: optionalFormText(net4TextSchema(NET4_FIELD_LIMITS.city)),
  codigo_postal: optionalFormText(net4TextSchema(10)),
  telefono: optionalFormText(net4PhoneSchema),
  web: optionalFormText(net4TextSchema(NET4_FIELD_LIMITS.website)),
  observaciones: optionalFormText(z.string().max(10_000, 'Las observaciones admiten un máximo de 10.000 caracteres.')),
  swift: optionalFormText(net4TextSchema(NET4_FIELD_LIMITS.swift)),
  industria: optionalFormText(z.string().trim()),
  industria_label: optionalFormText(z.string()),
  pais_label: optionalFormText(z.string()),
  tipo_ident: optionalFormText(z.string()),
  tipo_ident_label: optionalFormText(z.string()),
  tipo_ident_catalog_status: z.enum(['idle', 'loading', 'ready', 'error']).optional(),
};

const snapshotFields = {
  razon_social: net4TextSchema(NET4_FIELD_LIMITS.companyName).min(1, 'La razón social es obligatoria.'),
  nombre_comercial: net4TextSchema(NET4_FIELD_LIMITS.companyName).min(1, 'El nombre comercial es obligatorio.'),
  cif: net4TextSchema(NET4_FIELD_LIMITS.cif).min(1, 'La identificación fiscal es obligatoria.'),
  tipo_ident: z.string().min(1, 'Selecciona el tipo de identificación de NET4.'),
  pais: z.string().trim().min(1, 'El país es obligatorio.'),
  direccion: net4TextSchema(NET4_FIELD_LIMITS.address),
  localidad: net4TextSchema(NET4_FIELD_LIMITS.city),
  codigo_postal: net4TextSchema(10),
  provincia: z.string().trim(),
  municipio: z.string().trim(),
  telefono: net4PhoneSchema,
  web: net4TextSchema(NET4_FIELD_LIMITS.website),
  observaciones: z.string().max(10_000, 'Las observaciones admiten un máximo de 10.000 caracteres.'),
  swift: net4TextSchema(NET4_FIELD_LIMITS.swift),
  industria: z.string().trim(),
  nombre_contacto: net4TextSchema(NET4_FIELD_LIMITS.contactName).min(1, 'El nombre del contacto es obligatorio.'),
  cargo_contacto: net4TextSchema(NET4_FIELD_LIMITS.contactRole),
  telefono_contacto: net4PhoneSchema,
  email_contacto: net4TextSchema(NET4_FIELD_LIMITS.contactEmail).refine(
    (value) => value === '' || z.email().safeParse(value).success, 'Introduce un email válido.',
  ),
};

const closedWonClientDataSchema = z.object(snapshotFields).superRefine((data, ctx) => {
  const documentError = clientIdentificationError(data.cif, data.tipo_ident);
  if (documentError) ctx.addIssue({ code: 'custom', path: ['cif'], message: documentError });
  if (data.municipio && !data.provincia) {
    ctx.addIssue({ code: 'custom', path: ['provincia'], message: 'Indica la provincia del municipio.' });
  }
});

const closedWonClientUpdateSchema = z.object(snapshotFields).omit({ tipo_ident: true }).superRefine((data, ctx) => {
  if (data.municipio && !data.provincia) {
    ctx.addIssue({ code: 'custom', path: ['provincia'], message: 'Indica la provincia del municipio.' });
  }
});

const formSourceFields = {
  ...closedWonCompanyFields,
  cif: z.string().optional(),
  direccion_cliente_net4: z.string().optional(),
  nombre_contacto: z.string().optional(),
  cargo_contacto: z.string().optional(),
  telefono_contacto: z.string().optional(),
  email_contacto: z.string().optional(),
};
type FormSource = z.infer<z.ZodObject<typeof formSourceFields>>;
const rawFormSourceSchema = z.object(Object.fromEntries(
  Object.keys(formSourceFields).map((name) => [name, optionalFormText(z.string())]),
));

const recoveryScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const recoveryDataSchema = z.record(z.string(), recoveryScalarSchema);
const recoveryFields = [...new Set([
  ...Object.keys(closedWonCompanyFields), ...NET4_CLIENT_FIELDS,
  'net4_match_decision', 'net4_match_candidate', 'net4_match_candidate_label',
  'net4_match_candidate_status', 'net4_match_candidate_client_blocked',
  'net4_match_center_blocked', 'net4_match_manual_override',
  'contact_selection', 'contact_selection_label', 'contact_id', 'contact_id_label',
  'contacto_firmante_cargo', 'reports_to_contact_id', 'reports_to_contact_id_label',
  'reports_to_load_status',
  'net4_seller_owner_id', 'net4_seller_owner_label', 'net4_seller_owner_email',
  'net4_seller_owner_status', 'net4_seller_owner_scope', 'net4_seller_code', 'vendedor',
])];

export function recoverClosedWonFormValues(values: CamundaFormValues): CamundaFormValues {
  const parsed = recoveryDataSchema.safeParse(values.closed_won_recovery_data);
  if (!parsed.success) {
    if (values.net4_update_requested === true) {
      throw new Error('No se puede recuperar el formulario de revisión: faltan los datos completos del intento anterior.');
    }
    return values;
  }
  if (Object.keys(parsed.data).some((name) => !recoveryFields.includes(name))) {
    throw new Error('Los datos recuperados contienen campos no permitidos.');
  }
  return { ...values, ...parsed.data };
}

const snapshotRecoveryValues = (values: CamundaFormValues): Record<string, string | number | boolean | null> =>
  Object.fromEntries(recoveryFields.flatMap((name) => {
    const parsed = recoveryScalarSchema.safeParse(values[name]);
    return parsed.success ? [[name, parsed.data] as const] : [];
  }));

function snapshotFromForm(data: FormSource) {
  return {
    razon_social: data.razon_social ?? '', nombre_comercial: data.nombre_comercial ?? '',
    cif: normalizeClientIdentification(data.cif ?? ''), tipo_ident: data.tipo_ident ?? '',
    pais: data.pais ?? '', direccion: data.direccion_cliente_net4 ?? '',
    localidad: data.localidad ?? '', codigo_postal: data.codigo_postal ?? '',
    provincia: data.provincia ?? '', municipio: data.municipio ?? '', telefono: data.telefono ?? '',
    web: data.web ?? '', observaciones: data.observaciones ?? '', swift: data.swift ?? '',
    industria: data.industria ?? '', nombre_contacto: data.nombre_contacto ?? '',
    cargo_contacto: data.cargo_contacto ?? '', telefono_contacto: data.telefono_contacto ?? '',
    email_contacto: data.email_contacto ?? '',
  };
}

export function validateClosedWonClientData(data: FormSource & { net4_match_decision?: string; net4_update_requested?: boolean }, ctx: z.RefinementCtx) {
  const creating = data.net4_match_decision === 'not_exists';
  if (!creating && data.net4_update_requested !== true) return;
  if (creating && data.tipo_ident_catalog_status !== 'ready') {
    ctx.addIssue({ code: 'custom', path: ['tipo_ident'], message: 'Carga el catálogo de tipos de identificación de NET4 antes de continuar.' });
  }
  const result = (creating ? closedWonClientDataSchema : closedWonClientUpdateSchema).safeParse(snapshotFromForm(data));
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue({ code: 'custom', message: issue.message,
        path: issue.path[0] === 'direccion' ? ['direccion_cliente_net4'] : issue.path });
    }
  }
}

export function normalizeClosedWonForRpa(values: CamundaFormValues): CamundaFormValues {
  if (values.net4_match_decision !== 'not_exists' && values.net4_update_requested !== true) {
    return { ...values, closed_won_client_data: null, closed_won_recovery_data: null };
  }
  const data = rawFormSourceSchema.parse(values);
  data.telefono = compactNet4Phone(data.telefono ?? '');
  data.telefono_contacto = compactNet4Phone(data.telefono_contacto ?? '');
  const parsedSs = z.string().safeParse(values.numero_ss);
  const parsedFamily = z.string().safeParse(values.familia_de_productos);
  const numeroSs = parsedSs.success && parsedFamily.success && isEttProductFamily(parsedFamily.data)
    ? normalizeSecuritySocialNumber(parsedSs.data, securitySocialKind(data.tipo_ident ?? ''))
    : values.numero_ss;
  const normalized = { ...values, ...data, numero_ss: numeroSs, closed_won_client_data: snapshotFromForm(data) };
  return { ...normalized, closed_won_recovery_data: snapshotRecoveryValues(normalized) };
}
